/* eslint-disable */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { AlertTriangle, CheckCircle2, Info as InfoIcon, Loader2, Settings2 } from 'lucide-react';
import GrnHeader from '@/Pages/GRNPage/grnHeader';
import Info from '@/Components/InfoTab/Info';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import urls from '@/Constants/urls';
import { inputHelper, keyPressHandler, useQuery, getHigherUnitAndQuantity, convertToLowerUnit } from '@/Helpers';
import { useDispatch, useSelector } from 'react-redux';
import wastageAction from '@/Actions/wastage.action';
import useKeyPress from '@/Helpers/useKeyPress';
import notificationServices from '@/Services/notification.services';
import Timer from '@/Components/timer';
import moment from 'moment';
import { useAuth } from '@/Components/Auth/Login/auth';
import ConfirmationModal from '@/Components/Modals/MultiPoModal/ConfirmationModal';

const COLUMNS = [
    { title: 'PO ID', key: 'poId' },
    { title: 'Item Name', key: 'itemName', sortable: true },
    { title: 'Dispatched Qty', key: 'dispatchedQty', sortable: true },
    { title: 'Received Qty', key: 'receivedQty', sortable: true },
    { title: 'Qty Not Received', key: 'qtyNotReceived', sortable: true },
    { title: 'Recovered at Kitchen', key: 'recoveredAtKitchen', sortable: true },
    { title: 'Transit Loss Qty (After recovered)', key: 'transitLossQty', sortable: true },
    { title: 'Transit Loss % (After recovered)', key: 'transitLossPercent', sortable: true },
];

const FIELD_MAP = {
    'PO Detail': 'poDetail',
    'Transit Date & time': 'transitDateTime',
    'Overall Loss %': 'overallLossPercent',
    'Overall Dispatched Price': 'overallDispatchedPrice',
    'Overall Received Price': 'overallReceivedPrice',
    'Overall Loss Price': 'overallLossPrice',
};

function TransitLossDetail() {
    const history = useHistory();
    const [items, setItems] = useState([]);
    const { userConfig = {} } = useAuth()
    const { level } = userConfig?.userDetails || {};
    const [idx, setIdx] = useState(-1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const query = useQuery();
    const roundId = query.get('roundId');
    const keyPressed = useKeyPress();
    const loadingNotificationRef = useRef(null);
    const dispatch = useDispatch();
    const [viewMode, setViewMode] = useState(false);
    const [transitLoss, setTransitLoss] = useState({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const { singleTransitLoss, transitLossList, loading, faliureRequest, success } = useSelector((state) => state.wastage);
    const backUrl = history.location.state?.prevPath + '&flag=true' || urls.EXTENSION + urls.TRANSIT_LOSS;


    useEffect(() => {
        if (singleTransitLoss?.length > 0) {
            const isView = singleTransitLoss?.[0]?.status === 'ADJUSTED'
            setViewMode(isView);
        }
    }, [singleTransitLoss]);

    const poOrdersData = useMemo(() => {
        const poOrders = singleTransitLoss?.[0]?.poOrders || [];

        let totalDispatchedPrice = 0;
        let totalReceivedPrice = 0;
        let totalLossPrice = 0;
        let displayUnit = 'kg';

        poOrders.forEach((po) => {
            po?.items?.forEach((item) => {
                const fulfilled = item?.fulfilled || 0;
                const storeFulfilled = item?.storeFulfilled || 0;
                const basePrice = item?.productCost || 0;
                const effectivePrice = item?.taxPercentage ? basePrice * (1 + item.taxPercentage / 100) : basePrice;

                const [convertedFulfilled, unit] = getHigherUnitAndQuantity(item?.unit, fulfilled);
                const [convertedStoreFulfilled] = getHigherUnitAndQuantity(item?.unit, storeFulfilled);

                displayUnit = unit || 'kg';

                totalDispatchedPrice += convertedFulfilled * effectivePrice;
                totalReceivedPrice += convertedStoreFulfilled * effectivePrice;

                // Use declaredLossQty from API if available, otherwise calculate
                const calculatedLossQty = convertedFulfilled - convertedStoreFulfilled;
                const lossQtyToUse = item?.declaredLossQty !== undefined && item?.declaredLossQty !== null
                    ? Number(getHigherUnitAndQuantity(item?.unit, item?.declaredLossQty)[0])
                    : calculatedLossQty;

                totalLossPrice += lossQtyToUse * effectivePrice;
            });
        });

        const lossPercent = totalDispatchedPrice > 0 ? (totalLossPrice / totalDispatchedPrice) * 100 : 0;
        const date = moment(poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt).format('DD-MM-YYYY');
        const startime = moment(poOrders?.[0]?.PoStatusDateData?.[0]?.createdAt).format('hh:mm A');
        const endTime = moment(poOrders?.[0]?.PoStatusDateData?.[0]?.endedAt).format('hh:mm A');

        return {
            poCount: poOrders.length,
            totalDispatchedPrice,
            totalReceivedPrice,
            lossPrice: totalLossPrice,
            lossPercent,
            displayUnit,
            date,
            startime,
            endTime,
        };
    }, [singleTransitLoss]);

    const summaryData = useMemo(() => ({
        poDetail: `${poOrdersData?.poCount} POs`,
        transitDateTime: `${poOrdersData?.date} | ${poOrdersData?.startime}-${poOrdersData?.endTime}`,
        overallLossPercent: `${Math.max(0, Number(poOrdersData?.lossPercent || 0)).toFixed(2)}%`,
        overallDispatchedPrice: `₹${Number(poOrdersData?.totalDispatchedPrice || 0).toFixed(2)}`,
        overallReceivedPrice: `₹${Number(poOrdersData?.totalReceivedPrice || 0).toFixed(2)}`,
        overallLossPrice: `₹${Math.max(0, Number(poOrdersData?.lossPrice || 0)).toFixed(2)}`,
    }), [poOrdersData]);


    useEffect(() => {
        if (isSubmitting && success) {
            if (loadingNotificationRef.current) {
                notificationServices.closeNotification(loadingNotificationRef.current);
                loadingNotificationRef.current = null;
            }
            notificationServices.generateNotification({
                type: 'success',
                message: 'Transit loss has been accepted successfully!',
            });
            dispatch(wastageAction?.fetchTransitLossList({ data: { roundId } }, true));
            setIsSubmitting(false);
        }
    }, [success, isSubmitting]);

    useEffect(() => {
        if (isSubmitting && faliureRequest) {
            if (loadingNotificationRef.current) {
                notificationServices.closeNotification(loadingNotificationRef.current);
                loadingNotificationRef.current = null;
            }
            notificationServices.generateNotification({
                type: 'error',
                message: faliureRequest || 'Failed to accept transit loss. Please try again.',
            });
            setIsSubmitting(false);
        }
    }, [faliureRequest, isSubmitting]);

    useEffect(() => {
        if (roundId) {
            dispatch(wastageAction?.fetchTransitLossList({ data: { roundId } }, true));
        }
        const findIdx = transitLossList?.findIndex((item) => item?.roundId === roundId);
        if (findIdx !== -1) {
            setIdx(findIdx);
        }
    }, [roundId]);

    useEffect(() => {
        if (singleTransitLoss?.length > 0) {
            const transitQty = {};
            const variantMap = {};

            // First, collect all items and group by unique combination of poId and itemId
            singleTransitLoss?.[0]?.poOrders?.forEach((po) => {
                po?.items?.forEach((item) => {
                    const uniqueKey = `${item?.poId}_${item?.id}`;

                    if (variantMap[uniqueKey]) {
                        // Sum up values for same unique item (should rarely happen if id is PK)
                        variantMap[uniqueKey].fulfilled += (item?.fulfilled || 0);
                        variantMap[uniqueKey].storeFulfilled += (item?.storeFulfilled || 0);
                        variantMap[uniqueKey].declaredLossQty = item?.declaredLossQty || variantMap[uniqueKey].declaredLossQty;
                        variantMap[uniqueKey].declaredLossRecoveredQty = item?.declaredLossRecoveredQty || variantMap[uniqueKey].declaredLossRecoveredQty;
                        variantMap[uniqueKey].productCost = item?.productCost || variantMap[uniqueKey].productCost;
                        // Keep track of all item ids for this variant
                        variantMap[uniqueKey].itemIds.push(item?.id);
                        variantMap[uniqueKey].transitAdjustmentItemIds.push(item?.transitAdjustmentItemId);
                    } else {
                        // First occurrence of this unique combination
                        variantMap[uniqueKey] = {
                            ...item,
                            uniqueId: uniqueKey,
                            fulfilled: item?.fulfilled || 0,
                            storeFulfilled: item?.storeFulfilled || 0,
                            declaredLossQty: item?.declaredLossQty || 0,
                            declaredLossRecoveredQty: item?.declaredLossRecoveredQty || 0,
                            itemIds: [item?.id],
                            transitAdjustmentItemIds: [item?.transitAdjustmentItemId],
                        };
                    }
                });
            });

            // Convert grouped items to array and calculate transit loss
            const groupedItems = Object.values(variantMap).map((item) => {
                const uniqueKey = item?.uniqueId;
                const [convertedFulfilled, convertedUnit] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled);
                const [convertedStoreFulfilled] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled);
                const [convertedRecoveredQty] = getHigherUnitAndQuantity(item?.unit, item?.declaredLossRecoveredQty);

                // Calculate loss qty
                const calculatedLossQty = convertedFulfilled - convertedStoreFulfilled;
                const lossQtyToUse = item?.declaredLossQty
                    ? Number(getHigherUnitAndQuantity(item?.unit, item?.declaredLossQty)[0])
                    : calculatedLossQty;

                const lossPercent = convertedFulfilled > 0 ? (lossQtyToUse / convertedFulfilled) * 100 : 0;

                transitQty[uniqueKey] = {
                    transitLossQty: lossQtyToUse,
                    transitLossPercent: lossPercent,
                    unit: convertedUnit,
                };

                return {
                    ...item,
                    declaredLossRecoveredQty: convertedRecoveredQty,
                    displayUnit: convertedUnit,
                    convertedFulfilled,
                    convertedStoreFulfilled,
                };
            });

            groupedItems.sort((a, b) => {
                const aLoss = a.convertedFulfilled - a.convertedStoreFulfilled;
                const bLoss = b.convertedFulfilled - b.convertedStoreFulfilled;

                if (aLoss > 0 && bLoss > 0) return bLoss - aLoss;
                if (aLoss > 0) return -1;
                if (bLoss > 0) return 1;
                return 0;
            });

            setItems(groupedItems);
            setTransitLoss(transitQty);
        }
    }, [singleTransitLoss]);

    useEffect(() => {
        const qcData = transitLossList?.[idx] ?? null;
        if (
            (keyPressed === 'ArrowLeft' || keyPressed === 'ArrowRight') &&
            qcData !== null
        ) {
            const newListItem = keyPressHandler(
                keyPressed,
                transitLossList,
                idx
            );
            if (newListItem) {
                history.push({
                    pathname: `${urls.EXTENSION}${urls.TRANSIT_LOSS_DETAIL}`,
                    search: `roundId=${newListItem?.roundId}`,
                    state: {
                        prevPath: history?.location?.state?.prevPath,
                    },
                });
            }
        }
    }, [keyPressed]);

    const handleRecoveredChange = useCallback((obj, value) => {
        const recoveredValue = parseFloat(value) || null;

        setItems((prevItems) =>
            prevItems.map((item) =>
                item.uniqueId === obj.uniqueId ? { ...item, declaredLossRecoveredQty: recoveredValue } : item
            )
        );

        setTransitLoss((prev) => {
            // Use the already converted values from the grouped item
            const convertedFulfilled = obj?.convertedFulfilled || 0;
            const convertedStoreFulfilled = obj?.convertedStoreFulfilled || 0;
            const unit = obj?.displayUnit;

            const originalLossQty = convertedFulfilled - convertedStoreFulfilled;
            const transitLossQtyAfterRecovery = originalLossQty - (recoveredValue || 0);
            const transitLossPercentAfterRecovery = convertedFulfilled > 0
                ? (transitLossQtyAfterRecovery / convertedFulfilled) * 100
                : 0;

            return {
                ...prev,
                [obj?.uniqueId]: {
                    transitLossQty: transitLossQtyAfterRecovery,
                    transitLossPercent: transitLossPercentAfterRecovery,
                    unit: unit,
                },
            };
        });
    }, []);

    const handleAcceptAsTransitLoss = useCallback((itemsList, accept = true) => {
        if (isSubmitting) return;
        const errors = [];
        itemsList.forEach((item) => {
            const [dispatchQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled);
            const qtyNotReceived = dispatchQty - receiveQty;
            const recoveredQty = Number(item?.declaredLossRecoveredQty) || 0;
            if (recoveredQty > 0 && qtyNotReceived < recoveredQty) {
                errors.push({
                    variantId: item?.variantId,
                    productName: item?.productName,
                    variantName: item?.variantName,
                    message: `Recovery quantity (${recoveredQty}) cannot be greater than quantity not received (${qtyNotReceived?.toFixed(2)})`,
                });
            }
        });
        if (errors.length > 0) {
            notificationServices.generateNotification({
                type: 'error',
                message: errors[0]?.message,
            });
            return;
        }
        loadingNotificationRef.current = notificationServices.generateNotification({
            type: 'loading',
            message: accept ? 'Processing transit loss acceptance...' : 'Saving transit loss data...',
        });

        setIsSubmitting(true);
        setShowConfirmModal(false);
        const headers = {
            ...(singleTransitLoss?.[0]?.transitAdjustmentId && { transitAdjustmentId: singleTransitLoss?.[0]?.transitAdjustmentId }),
            storeId: singleTransitLoss?.[0]?.poOrders?.[0]?.fromStore,
            vehicleNo: singleTransitLoss?.[0]?.vehicleNo,
            roundId: singleTransitLoss?.[0]?.roundId,
            recievingDate: moment(singleTransitLoss?.[0]?.poOrders?.[0]?.PoStatusDateData?.[0]?.endedAt).format('YYYY-MM-DD'),
            poIds: singleTransitLoss?.[0]?.poOrders?.map((item) => item?.id),
            ...(accept ? { adjustmentBy: 'manual' } : {}),
            accept: accept,
        }
        const items = itemsList.map((item) => {
            const unit = item?.originalUnit || item?.unit;
            const [dispatchQty] = getHigherUnitAndQuantity(item?.unit, item?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(item?.unit, item?.storeFulfilled);
            const isExcessReceived = receiveQty > dispatchQty;

            // If received > dispatched, set recovered and loss to 0
            const recoveredValue = isExcessReceived ? 0 : convertToLowerUnit(unit, item?.declaredLossRecoveredQty);
            const lossQty = isExcessReceived ? 0 : transitLoss[item?.variantId]?.transitLossQty;

            return {
                receivedQty: item?.storeFulfilled,
                dispatchedQty: item?.fulfilled,
                declaredLossQty: lossQty,
                declaredRecoveredQty: recoveredValue,
                variantId: item?.variantId,
                ...(item?.transitAdjustmentItemId && { transitAdjustmentItemId: item?.transitAdjustmentItemId }),
                productId: item?.productId,
            }
        });
        const payload = {
            ...headers,
            items,
        };
        dispatch(wastageAction?.saveTransitLossRecoverdQty({ data: payload }, true));
    }, [dispatch, singleTransitLoss, isSubmitting, transitLoss]);

    const hasTransitLoss = useMemo(() => {
        return items.some(item => (item.fulfilled || 0) > (item.storeFulfilled || 0));
    }, [items]);

    const buttons = useMemo(() => {
        const result = [];
        if (!hasTransitLoss) {
            result.push({
                label: 'No Transit Loss Yet',
                icon: <CheckCircle2 size={16} />,
                style: 'text-green-700 cursor-default shadow-none pointer-events-none px-3 h-9',
                noLevelSpecific: true,
            })
        } else {
            result.push({
                label: 'Detected Transit Loss',
                icon: <AlertTriangle size={16} />,
                style: 'text-red-500 cursor-default shadow-none pointer-events-none px-3 h-9',
                noLevelSpecific: true,
            })
            result.push({
                label: isSubmitting ? 'Saving...' : 'Save Transit Loss',
                handleClick: () => setShowConfirmModal(true),
                style: `bg-blue-600 text-white hover:bg-blue-700 border-blue-600 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`,
                noLevelSpecific: true,
                disabled: isSubmitting,
            })
        }
        return result;
    }, [items, handleAcceptAsTransitLoss, isSubmitting, transitLoss, hasTransitLoss]);


    const confirmModalButtons = useMemo(() => [
        {
            label: 'Save Draft',
            onClick: () => {
                handleAcceptAsTransitLoss(items, false);
            },
            className: 'bg-gray-500 text-white hover:bg-gray-600',
        },
        {
            label: 'Accept Transit Loss',
            onClick: () => {
                handleAcceptAsTransitLoss(items, true);
            },
            className: 'bg-teal-600 text-white hover:bg-teal-700',
        },
    ], [items, handleAcceptAsTransitLoss]);

    const heading = useMemo(() => ({
        key: 'Truck No',
        value: singleTransitLoss?.[0]?.vehicleNo,
    }), [singleTransitLoss]);


    const customRowData = useMemo(() => ({
        poId: (obj) => (
            <span className="text-blue-600 cursor-pointer hover:underline"
                onClick={() => window.open(`${urls.EXTENSION}${urls.PO_LIST}?poId=${obj?.poId}&paramNavSelected=${true}`, '_blank')}
            >
                {obj?.poId}
            </span>
        ),
        itemName: (obj) => (
            <span className="text-gray-600 text-[12px]">
                {`${obj?.productName} (${obj?.variantName})`}
            </span>
        ),
        dispatchedQty: (obj) => {
            const [convertedQty, convertedUnit] = getHigherUnitAndQuantity(obj?.unit, obj?.fulfilled || 0);
            return (
                <span className="text-gray-600 text-[12px]">{convertedQty} {convertedUnit}</span>
            );
        },
        receivedQty: (obj) => {
            const [convertedQty, convertedUnit] = getHigherUnitAndQuantity(obj?.unit, obj?.storeFulfilled || 0);
            return (
                <span className="text-gray-600 text-[12px]">{convertedQty} {convertedUnit}</span>
            );
        },
        qtyNotReceived: (obj) => {
            const [dispatchQty, convertedUnit] = getHigherUnitAndQuantity(obj?.unit, obj?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(obj?.unit, obj?.storeFulfilled);
            // If received > dispatched, show 0
            const qtyNotReceived = receiveQty > dispatchQty ? 0 : dispatchQty - receiveQty;
            return (
                <span className="text-red-500 font-medium text-[12px]">
                    {qtyNotReceived?.toFixed(2) ?? 0} {convertedUnit ?? ''}
                </span>
            );
        },
        recoveredAtKitchen: (obj) => {
            const [dispatchQty] = getHigherUnitAndQuantity(obj?.unit, obj?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(obj?.unit, obj?.storeFulfilled);
            // If received > dispatched, show 0 and make non-editable
            const isExcessReceived = receiveQty > dispatchQty;

            if (isExcessReceived) {
                return (
                    <div className="flex items-center gap-2">
                        <span className="text-gray-600 text-[12px]">0</span>
                        <span className="text-gray-600">{obj?.displayUnit}</span>
                    </div>
                );
            }

            const hasLoss = (Number(dispatchQty || 0) - Number(receiveQty || 0)) > 0.0001;
            const isReadOnly = viewMode || level === 1 || !hasLoss;

            return (
                <div className="flex items-center gap-2">
                    {isReadOnly ? <span className="text-gray-600 text-[12px]">{obj?.declaredLossRecoveredQty || 0}</span> : (
                        <input
                            type="number"
                            value={obj?.declaredLossRecoveredQty ?? ''}
                            onChange={(e) => handleRecoveredChange(obj, e.target.value)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            placeholder="0"
                        />
                    )}
                    <span className="text-gray-600">{obj?.displayUnit}</span>
                </div>
            );
        },

        transitLossQty: (obj) => {
            const [dispatchQty] = getHigherUnitAndQuantity(obj?.unit, obj?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(obj?.unit, obj?.storeFulfilled);
            // If received > dispatched, show 0
            if (receiveQty > dispatchQty) {
                return <span className="text-gray-500">0.00 {obj?.displayUnit ?? ''}</span>;
            }
            const itemLoss = transitLoss?.[obj?.uniqueId];
            return (
                <span className="text-gray-500">{Math.max(0, itemLoss?.transitLossQty)?.toFixed(2) ?? 0} {itemLoss?.unit ?? ''}</span>
            );
        },
        transitLossPercent: (obj) => {
            const [dispatchQty] = getHigherUnitAndQuantity(obj?.unit, obj?.fulfilled);
            const [receiveQty] = getHigherUnitAndQuantity(obj?.unit, obj?.storeFulfilled);
            // If received > dispatched, show 0
            if (receiveQty > dispatchQty) {
                return <span className="text-gray-500">0.00 %</span>;
            }
            const itemLoss = transitLoss?.[obj?.uniqueId];
            return (
                <span className="text-gray-500">{Math.max(0, itemLoss?.transitLossPercent)?.toFixed(2) ?? 0} %</span>
            );
        },
    }), [handleRecoveredChange, transitLoss, viewMode]);

    const sortValueInfo = useMemo(() => ({
        itemName: {
            type: 'string',
            customValue: true,
            value: (obj) => `${obj?.productName} (${obj?.variantName})`,
        },
        dispatchedQty: {
            type: 'number',
            customValue: true,
            value: (obj) => obj?.convertedFulfilled || 0,
        },
        receivedQty: {
            type: 'number',
            customValue: true,
            value: (obj) => obj?.convertedStoreFulfilled || 0,
        },
        qtyNotReceived: {
            type: 'number',
            customValue: true,
            value: (obj) => (obj?.convertedFulfilled || 0) - (obj?.convertedStoreFulfilled || 0),
        },
        recoveredAtKitchen: {
            type: 'number',
            customValue: true,
            value: (obj) => obj?.declaredLossRecoveredQty || 0,
        },
        transitLossQty: {
            type: 'number',
            customValue: true,
            value: (obj) => transitLoss?.[obj?.uniqueId]?.transitLossQty || 0,
        },
        transitLossPercent: {
            type: 'number',
            customValue: true,
            value: (obj) => transitLoss?.[obj?.uniqueId]?.transitLossPercent || 0,
        },
    }), [transitLoss]);

    const navigateQC = useCallback((e) => {
        const { dataset } = inputHelper(e);
        const newListItem = dataset.type === 'next'
            ? transitLossList?.[idx + 1]
            : transitLossList?.[idx - 1];
        history.push({
            pathname: `${urls.EXTENSION}${urls.TRANSIT_LOSS_DETAIL}`,
            search: `roundId=${newListItem?.roundId}`,
            state: {
                prevPath: history?.location?.state?.prevPath,
            },
        });
    }, [transitLossList, idx, history]);

    if (loading && !singleTransitLoss?.length) {
        return (
            <div className="flex flex-col h-full">
                <GrnHeader
                    url={backUrl}
                    title="Back to Logs"
                />
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                        <span className="text-gray-600 text-sm">Loading transit loss details...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                heading="Transit Loss Confirmation"
                message="Would you like to accept this as transit loss or just save the recovery data?"
                customButton={confirmModalButtons}
                showCrossBtn={true}
            />

            {/* Header */}
            <GrnHeader
                url={backUrl}
                title="Back to Logs"
                navigatePrevToolTip="Previous Round"
                navigateLabel="Next Round"
                navigateNextToolTip={singleTransitLoss?.[0]?.vehicleNo}
                navigateQC={navigateQC}
                idx={idx}
                listData={transitLossList}
                id={singleTransitLoss?.[0]?.vehicleNo}
            />

            {/* Main Content */}
            <div className="flex-1 p-6 mt-16">
                {/* Info Note */}
                <div className="flex items-center justify-between gap-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <InfoIcon className="text-blue-600 flex-shrink-0" size={18} />
                        <span className="text-sm text-blue-700">
                            This entry will auto-accept on {moment().add(1, 'day').format('DD-MM-YYYY')} | 12:00 AM. Please review and update adjustments before this.
                        </span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-yellow-300 to-yellow-200 text-orange-500 text-sm font-medium rounded-full shadow-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" strokeWidth="2" />
                            <path strokeWidth="2" strokeLinecap="round" d="M12 6v6l4 2" />
                        </svg>
                        <Timer />
                    </div>
                </div>

                {/* Summary Info */}
                <Info
                    heading={heading}
                    fieldMap={FIELD_MAP}
                    data={summaryData}
                    buttons={viewMode ? [] : buttons}
                />

                {/* Items Table */}
                <div className="h-[calc(100vh-450px)] relative">
                    <VariantTable
                        data={items}
                        columns={COLUMNS}
                        customRowData={customRowData}
                        sortValueInfo={sortValueInfo}
                        externalLoading={loading}
                    />
                </div>
            </div>
        </div>
    );
}

export default TransitLossDetail;